import { Controller } from "stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = [
    "scheduleButton", 
    "makeUpReassignmentContainer", 
    "rowTemplate"
  ];

  appendQueryParams(params) {
    const pageParams = new URLSearchParams(window.location.search);
    const guardianPupilId = pageParams.get("guardian_pupil")?.trim();
    if (guardianPupilId) params.append("guardian_pupil", guardianPupilId);
    const locale = pageParams.get("locale")?.trim();
    if (locale) params.append("locale", locale);
  }

  async showScheduleClassroomMakeUpQuickLook(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    if (!attendanceId) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const params = new URLSearchParams();
    params.append("attendance_id", attendanceId);
    this.appendQueryParams(params);

    const response = await fetch(`/us/dashboard/student/classroom/quick-look/schedule?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (!response.ok) return;

    const quickLookElement = document.querySelector("#driving-school-student-dashboard-quick-look");
    if (!quickLookElement) return;

    quickLookElement.classList.remove("u-hidden");
    const responseBody = await response.text();

    if (Turbo && typeof Turbo.renderStreamMessage === "function") {
      Turbo.renderStreamMessage(responseBody);
    }
  }

  async showScheduleClassroomSessionQuickLook(event) {
    event.preventDefault();
    
    const attendanceId = this.data.get("attendanceId")
    if (!attendanceId) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const params = new URLSearchParams();
    params.append("attendance_id", attendanceId);
    this.appendQueryParams(params);

    const response = await fetch(`/us/dashboard/student/classroom/quick-look/session/schedule?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (!response.ok) return;

    const quickLookElement = document.querySelector("#driving-school-student-dashboard-session-quick-look");
    if (!quickLookElement) return;

    quickLookElement.classList.remove("u-hidden");
    const responseBody = await response.text();
    
    if (Turbo && typeof Turbo.renderStreamMessage === "function") {
      Turbo.renderStreamMessage(responseBody);
    }
  }

  makeUpRowElements() {
    if (!this.hasMakeUpReassignmentContainerTarget) {
      return []
    }

    return Array.from(this.makeUpReassignmentContainerTarget.querySelectorAll("[data-row-key]"));
  }

  onSessionSelected(event) {
    if (!this.hasScheduleButtonTarget) return;

    const selectedId = event?.detail?.id?.toString()?.trim() || "";
    const hasValue = selectedId.length > 0;
    this.scheduleButtonTarget.disabled = !hasValue;
    this.scheduleButtonTarget.setAttribute("aria-disabled", hasValue ? "false" : "true");
  }

  onMakeUpSessionSelected(event) {
    if (!this.hasScheduleButtonTarget || !this.hasMakeUpReassignmentContainerTarget) return;

    this.removeTrailingBlankRows();

    const requiredMinutes = this.getRequiredMinutes()
    const requiredMinutesMet = this.getMadeUpMinutes() >= requiredMinutes
    const everyRowSelected = this.makeUpRowElements().every((rowElement) => !this.makeUpRowIsBlank(rowElement))

    if (!requiredMinutesMet && everyRowSelected) {
      this.addMakeUpRow()
    } 

    if (requiredMinutesMet) {
      this.clearRequiredHoursEl();
    } 

    const madeUpMinutes = this.getMadeUpMinutes();

    // bit hacky. this adds back the remaining hours to be covered message
    //  when the user clears the select box.
    if (event.detail.dispatchItem.id == '') {
      this.clearRequiredHoursEl();
      if (madeUpMinutes != 0) {
        this.addRequiredHoursEl();
      }
    }

    this.scheduleButtonTarget.disabled = !requiredMinutesMet
    this.scheduleButtonTarget.setAttribute("aria-disabled", requiredMinutesMet ? "false" : "true");
    

    this.syncTakenSessions();
  }

  syncTakenSessions() {
    const rowElements = this.makeUpRowElements();
    const selectedIds = rowElements.map((el) => 
      el.querySelector(".Selector-item--selected")?.dataset.itemId
    )

    rowElements.forEach((el, i) => {
      const takenIds = selectedIds.filter((sessionId, otherIndex) => sessionId && otherIndex !== i)
      el.querySelectorAll(".Selector-item").forEach((item) => {
        item.classList.toggle("u-hidden", takenIds.includes(item.dataset.itemId));
      })
    })
  }

  makeUpRowIsBlank(rowElement) {
    return !rowElement.querySelector(".Selector-item--selected");
  }

  getMadeUpMinutes() {
    const totalDuration = this.makeUpRowElements()
      .map((rowElement) => (rowElement.querySelector(".Selector-item--selected")))
      .reduce((sum, item) => sum + this.itemDurationInMinutes(item), 0)

    return totalDuration
  }

  itemDurationInMinutes(item) {
    if (!item) return 0;

    try {
      return parseInt(JSON.parse(item.dataset.itemMetadata || "{}").duration || 0, 10);
    } catch {
      return 0;
    }
  }

  getRequiredMinutes() {
    return parseInt(this.makeUpReassignmentContainerTarget.dataset.requiredMinutes, 10)
  }

  clearRequiredHoursEl() {
    const existingRequiredHoursEl = document.getElementById("required_hours")
    if (existingRequiredHoursEl) {
      existingRequiredHoursEl.remove()
    }
  }

  formatDuration(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;

    if (hours === 0) return `${remainder}mins`;
    if (remainder === 0) return `${hours}h`;
    return `${hours}h ${remainder}mins`;
  }

  addRequiredHoursEl() {
    const minutesLeft = this.getRequiredMinutes() - this.getMadeUpMinutes();
    const minutesLeftHtml = `<span class="u-textRedMid" id="required_hours">Requires ${this.formatDuration(minutesLeft)} more to be assigned.</span>`
    this.makeUpReassignmentContainerTarget.insertAdjacentHTML("beforeend", minutesLeftHtml);
  }

  addMakeUpRow() {
    this.clearRequiredHoursEl();

    const rowKey = this.nextRowKey();
    const rowKeyHtml = this.rowTemplateTarget.innerHTML.replaceAll("__ROW_KEY__", rowKey);

    this.makeUpReassignmentContainerTarget.insertAdjacentHTML("beforeend", rowKeyHtml);
    this.addRequiredHoursEl();
  }

  removeTrailingBlankRows() {
    let rowElements = this.makeUpRowElements();

    while (rowElements.length > 1) {
      const lastRow = rowElements[rowElements.length - 1];
      const previousRow = rowElements[rowElements.length - 2];
      if (!this.makeUpRowIsBlank(lastRow) || !this.makeUpRowIsBlank(previousRow)) break;

      lastRow.remove();
      rowElements = this.makeUpRowElements();
    }
  }

  nextRowKey() {
    const keys = this.makeUpRowElements()
      .map((rowElement) => rowElement.dataset.rowKey || "")
      .map((rowKey) => {
        return rowKey ? parseInt(rowKey) : null;
      })

    const maxKey = keys.length > 0 ? Math.max(...keys) + 1 : 0
    return maxKey
  }

  selectedMakeUpSessionIds() {
    return this.makeUpRowElements()
      .map((rowElement) => rowElement.querySelector("[data-simple-selector-target='hiddenInput']"))
      .map((hiddenInput) => (hiddenInput?.value || "").trim())
      .filter((sessionId) => sessionId.length > 0);
  }

  async scheduleClassroomMakeUp(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    const selectedSessionIds = this.selectedMakeUpSessionIds();
    if (!attendanceId || selectedSessionIds.length === 0) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new URLSearchParams();
    body.append("attendance_id", attendanceId);
    selectedSessionIds.forEach((sessionId) => {
      body.append("student_make_up_classroom_session_ids[]", sessionId);
    });
    this.appendQueryParams(body);

    const response = await fetch("/us/dashboard/student/classroom/quick-look/schedule", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: body.toString()
    });

    if (!response.ok) return;

    const responseBody = await response.text();
    if (Turbo && typeof Turbo.renderStreamMessage === "function") {
      Turbo.renderStreamMessage(responseBody);
    }
  }

  async scheduleClassroomSession(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    const selectedSessionId = document.querySelector("#student_classroom_session_id")?.value;
    if (!attendanceId || !selectedSessionId) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new URLSearchParams();
    body.append("attendance_id", attendanceId);
    body.append("student_classroom_session_id", selectedSessionId);
    this.appendQueryParams(body);

    const response = await fetch("/us/dashboard/student/classroom/quick-look/session/schedule", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: body.toString()
    });

    if (!response.ok) return;

    const responseBody = await response.text();
    if (Turbo && typeof Turbo.renderStreamMessage === "function") {
      Turbo.renderStreamMessage(responseBody);
    }
  }

  closeQuickLook(event) {
    if (event) event.preventDefault();
   
    const quickLookElement = document.querySelector("#driving-school-student-dashboard-quick-look")
    const sessionQuickLookElement = document.querySelector("#driving-school-student-dashboard-session-quick-look")

    if (!quickLookElement && !sessionQuickLookElement) return;

    for (const el of [quickLookElement, sessionQuickLookElement]) {
      if (el) el.classList.add("u-hidden")
    }
  
  }
}
